import * as React from 'react';
import { services } from '../../../services';
import { useBackgroundPromiseWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../helpers/hooks/useDownload';
import { StandartSolutionFolder } from '../models/StandartSolutionFolder';
import { StandartSolution } from '../models/StandartSolution';
import styles from './StandartSolutionFolderList.module.css';

const CAT_STYLE = { icon: 'fa-folder', color: '#6e6e73', tint: '#f5f5f7' };

const EXT_STYLES: Record<string, { icon: string; color: string; bg: string }> = {
  DWG: { icon: 'fa-compass-drafting', color: '#2f5fb0', bg: '#eaf1fd' },
  PDF: { icon: 'fa-file-pdf', color: '#c9503b', bg: '#fdeeeb' },
  DOC: { icon: 'fa-file-word', color: '#3a63b5', bg: '#eef1fc' },
  DOCX: { icon: 'fa-file-word', color: '#3a63b5', bg: '#eef1fc' },
  XLS: { icon: 'fa-file-excel', color: '#2b8a52', bg: '#e9f6ee' },
  XLSX: { icon: 'fa-file-excel', color: '#2b8a52', bg: '#e9f6ee' },
};

const DEFAULT_EXT_STYLE = { icon: 'fa-file', color: '#6e6e73', bg: '#f5f5f7' };

const SORT_FIELDS: Record<string, string> = {
  name: 'Title',
  date: 'Modified',
};

const PAGE_SIZE = 12;

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

function formatDate(d?: Date | string): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function parseExt(title?: string): string {
  if (!title) return '';
  const parts = title.split('.');
  return parts.length > 1 ? parts.pop()!.toUpperCase() : '';
}

function getExtStyle(ext: string) {
  return EXT_STYLES[ext] || DEFAULT_EXT_STYLE;
}

export const StandartSolutionFolderList: React.FC = () => {
  const [promiseWrapper] = useBackgroundPromiseWrapper();
  const { downloadAttachment, downloadFile, isDownloading } = useDownload();
  const [folders, setFolders] = React.useState<StandartSolutionFolder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = React.useState<number | null>(null);

  const [files, setFiles] = React.useState<StandartSolution[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(1);

  const [sortKey, setSortKey] = React.useState<'name' | 'date'>('name');
  const [sortDir, setSortDir] = React.useState<1 | -1>(1);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<StandartSolution[]>([]);

  const [isMobile, setIsMobile] = React.useState(false);
  const [catPickerOpen, setCatPickerOpen] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 860);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Load folders
  React.useEffect(() => {
    promiseWrapper(
      () => services.standartSolutionFolderService.fetch({ IsDeleted: false, Take: 9999, FilterByParent: true }),
      (result: StandartSolutionFolder[]) => {
        const active = (result || []).filter((f) => !f.IsDeleted);
        setFolders(active);
        if (active.length > 0) setSelectedFolderId(active[0].Id ?? null);
      },
    );
  }, []);

  const fetchFiles = React.useCallback(
    (folderId: number, pg: number, sk: 'name' | 'date', sd: 1 | -1) => {
      const skip = (pg - 1) * PAGE_SIZE;
      promiseWrapper(
        () =>
          services.standartSolutionService.fetchPaged({
            FolderIds: [folderId],
            IsDeleted: false,
            Take: PAGE_SIZE,
            Skip: skip,
            Sorts: [{ Field: SORT_FIELDS[sk], Desc: sd === -1 }],
          }),
        (result: any) => {
          setFiles((result.Items ?? result.items ?? []) as StandartSolution[]);
          setTotalCount(result.TotalCount ?? result.totalCount ?? 0);
        },
      );
    },
    [promiseWrapper],
  );

  // Load files when folder changes
  React.useEffect(() => {
    if (selectedFolderId == null) return;
    setFiles([]);
    setTotalCount(0);
    setPage(1);
    setSortKey('name');
    setSortDir(1);
    setSearchQuery('');
    setIsSearching(false);
    setSearchResults([]);

    fetchFiles(selectedFolderId, 1, 'name', 1);
  }, [selectedFolderId, fetchFiles]);

  // Search within folder
  React.useEffect(() => {
    if (!searchQuery.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      promiseWrapper(
        () =>
          services.standartSolutionService.fetch({
            IsDeleted: false,
            Take: 100,
            FolderIds: selectedFolderId ? [selectedFolderId] : undefined,
            TitleContains: searchQuery.trim(),
            Sorts: [{ Field: SORT_FIELDS[sortKey], Desc: sortDir === -1 }],
          }),
        (result: StandartSolution[]) => {
          if (cancelled) return;
          setIsSearching(true);
          setSearchResults(result || []);
        },
      );
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, selectedFolderId, sortKey, sortDir]);

  const selectedFolder = folders.find((f) => f.Id === selectedFolderId);

  const handlePageChange = (newPage: number) => {
    if (selectedFolderId == null) return;
    setPage(newPage);
    fetchFiles(selectedFolderId, newPage, sortKey, sortDir);
  };

  const handleSort = (key: 'name' | 'date') => {
    const newDir: 1 | -1 = sortKey === key ? (sortDir === 1 ? -1 : 1) : 1;
    const newPage = 1;
    setSortKey(key);
    setSortDir(newDir);
    setPage(newPage);

    if (selectedFolderId != null) {
      if (isSearching && searchQuery.trim()) {
        promiseWrapper(
          () =>
            services.standartSolutionService.fetch({
              IsDeleted: false,
              Take: 100,
              FolderIds: [selectedFolderId],
              TitleContains: searchQuery.trim(),
              Sorts: [{ Field: SORT_FIELDS[key], Desc: newDir === -1 }],
            }),
          (result: StandartSolution[]) => {
            setSearchResults(result || []);
          },
        );
      } else {
        fetchFiles(selectedFolderId, newPage, key, newDir);
      }
    }
  };

  const displayFiles = isSearching ? searchResults : files;

  const effectiveTotal = isSearching ? searchResults.length : totalCount;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / PAGE_SIZE));

  const n = selectedFolder?.SolutionsCount ?? 0;
  const countLabel = isSearching
    ? `${searchResults.length} ${plural(searchResults.length, 'файл', 'файла', 'файлов')} найдено из ${n}`
    : `${n} ${plural(n, 'файл', 'файла', 'файлов')}`;

  const from = effectiveTotal === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, effectiveTotal);

  const pagerSeq: (number | '…')[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
      pagerSeq.push(p);
    } else if (pagerSeq[pagerSeq.length - 1] !== '…') {
      pagerSeq.push('…');
    }
  }

  return (
    <div className={styles.root}>
      {/* Heading */}
      <div className={styles.heading}>
        <h1 className={styles.title}>Типовые проекты и решения</h1>
        <p className={styles.subtitle}>Библиотека готовых узлов и типовых решений X5.</p>
      </div>

      <div className={styles.layout}>
        {!isMobile ? (
          <div className={styles.sidebar}>
            <div className={styles.sidebarTitle}>Разделы</div>
            <div className={styles.sidebarList}>
              {folders.map((f) => {
                const s = CAT_STYLE;
                const selected = f.Id === selectedFolderId;
                return (
                  <button
                    key={f.Id}
                    className={styles.catItem}
                    style={selected ? { background: 'rgba(58,174,58,.10)' } : undefined}
                    onClick={() => setSelectedFolderId(f.Id ?? null)}
                  >
                    <span className={styles.catIcon} style={{ background: s.tint, color: s.color }}>
                      <i className={`fa-solid ${s.icon}`} />
                    </span>
                    <span className={styles.catName} style={{ fontWeight: selected ? 650 : 500 }}>
                      {f.Title}
                    </span>
                    <span
                      className={styles.catCount}
                      style={{
                        background: selected ? '#3aae3a' : '#eef0f2',
                        color: selected ? '#fff' : '#86868b',
                      }}
                    >
                      {f.SolutionsCount ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {!catPickerOpen ? (
              <div className={styles.mobileCatPicker} onClick={() => setCatPickerOpen(true)}>
                <div className={styles.mobileCatPickerHeader}>
                  <span className={styles.mobileCatPickerLabel}>Раздел</span>
                  <span className={styles.mobileCatPickerToggle}>
                    Нажмите, чтобы изменить <i className="fa-solid fa-chevron-down" style={{ fontSize: 10 }} />
                  </span>
                </div>
                {selectedFolder && (
                  <div className={styles.mobileCatPickerBody}>
                    <span
                      className={styles.catIcon}
                      style={{
                        background: CAT_STYLE.tint,
                        color: CAT_STYLE.color,
                      }}
                    >
                      <i className={`fa-solid ${CAT_STYLE.icon}`} />
                    </span>
                    <div className={styles.mobileCatPickerInfo}>
                      <div className={styles.mobileCatPickerName}>{selectedFolder.Title}</div>
                      <div className={styles.mobileCatPickerCount}>
                        {selectedFolder.SolutionsCount ?? 0}{' '}
                        {plural(selectedFolder.SolutionsCount ?? 0, 'файл', 'файла', 'файлов')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.mobileSidebarCard}>
                <button className={styles.mobileSidebarHeader} onClick={() => setCatPickerOpen(false)}>
                  <span className={styles.mobileSidebarTitle}>Разделы</span>
                  <i className={`fa-solid fa-chevron-up ${styles.mobileSidebarCaret}`} />
                </button>
                <div className={styles.mobileSidebarList}>
                  {folders.map((f) => {
                    const s = CAT_STYLE;
                    const selected = f.Id === selectedFolderId;
                    return (
                      <button
                        key={f.Id}
                        className={styles.catItem}
                        style={selected ? { background: 'rgba(58,174,58,.10)' } : undefined}
                        onClick={() => {
                          setSelectedFolderId(f.Id ?? null);
                          setCatPickerOpen(false);
                        }}
                      >
                        <span className={styles.catIcon} style={{ background: s.tint, color: s.color }}>
                          <i className={`fa-solid ${s.icon}`} />
                        </span>
                        <span className={styles.catName} style={{ fontWeight: selected ? 650 : 500 }}>
                          {f.Title}
                        </span>
                        <span
                          className={styles.catCount}
                          style={{
                            background: selected ? '#3aae3a' : '#eef0f2',
                            color: selected ? '#fff' : '#86868b',
                          }}
                        >
                          {f.SolutionsCount ?? 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Right: files panel */}
        {selectedFolder && (
          <div className={styles.panel}>
            {/* Panel header */}
            <div className={styles.panelHeader}>
              <span
                className={styles.panelIcon}
                style={{
                  background: CAT_STYLE.tint,
                  color: CAT_STYLE.color,
                }}
              >
                <i className={`fa-solid ${CAT_STYLE.icon}`} />
              </span>
              <div className={styles.panelInfo}>
                <div className={styles.panelName}>{selectedFolder.Title}</div>
                <div className={styles.panelCount}>{countLabel}</div>
              </div>
              <button
                className={styles.btnDownloadAll}
                disabled={isDownloading}
                onClick={() => selectedFolder.Id != null && void downloadFile(`StandartSolutions/DownloadAll?folderId=${selectedFolder.Id}`)}
              >
                <i className={isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`} style={{ fontSize: 13, color: '#3aae3a' }} />
                <span>{isDownloading ? `Скачивание…` : `Скачать всё`}</span>
              </button>
            </div>

            <div className={styles.searchWrap}>
              <i className={`fa-solid fa-magnifying-glass ${styles.searchIcon}`} />
              <input
                className={styles.searchInput}
                placeholder="Поиск по названию…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className={styles.searchClear} onClick={() => setSearchQuery('')}>
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>

            {!isMobile ? (
              <div className={styles.tableScroll}>
                <div className={styles.tableScrollInner}>
                  <div className={styles.tableHeader}>
                    <button
                      className={`${styles.tableHeaderCell} ${sortKey === 'name' ? styles.tableHeaderCellActive : ''}`}
                      onClick={() => handleSort('name')}
                    >
                      Название файла{' '}
                      <i
                        className={`fa-solid ${
                          sortKey === 'name'
                            ? sortDir === 1
                              ? 'fa-arrow-up-short-wide'
                              : 'fa-arrow-down-wide-short'
                            : 'fa-sort'
                        } ${styles.sortIcon}`}
                        style={{ color: sortKey === 'name' ? '#3aae3a' : '#c7c7cc' }}
                      />
                    </button>
                    <div className={styles.tableHeaderCell} style={{ cursor: 'default' }}>Шифр</div>
                    <div className={styles.tableHeaderCell} style={{ cursor: 'default' }}>Формат</div>
                    <button
                      className={`${styles.tableHeaderCell} ${sortKey === 'date' ? styles.tableHeaderCellActive : ''}`}
                      onClick={() => handleSort('date')}
                    >
                      Обновлён{' '}
                      <i
                        className={`fa-solid ${
                          sortKey === 'date'
                            ? sortDir === 1
                              ? 'fa-arrow-up-short-wide'
                              : 'fa-arrow-down-wide-short'
                            : 'fa-sort'
                        } ${styles.sortIcon}`}
                        style={{ color: sortKey === 'date' ? '#3aae3a' : '#c7c7cc' }}
                      />
                    </button>
                    <div className={styles.tableActionsHead} aria-hidden />
                  </div>

                  {displayFiles.length === 0 ? (
                    <div className={styles.emptyState}>
                      <i className={`fa-regular fa-folder-open ${styles.emptyIcon}`} />
                      <span className={styles.emptyLabel}>
                        {isSearching
                          ? `По запросу «${searchQuery}» ничего не найдено`
                          : 'В этом разделе пока нет файлов'}
                      </span>
                    </div>
                  ) : (
                    displayFiles.map((file) => {
                      const ext = parseExt(file.Attachment?.Title);
                      const es = getExtStyle(ext);
                      return (
                        <div key={file.Id} className={styles.tableRow}>
                          <div className={styles.fileInfo}>
                            <span className={styles.fileIcon} style={{ background: es.bg, color: es.color }}>
                              <i className={`fa-solid ${es.icon}`} />
                            </span>
                            <span className={styles.fileName} title={file.Title}>
                              {file.Title}
                            </span>
                          </div>
                          <div className={styles.cipherCell} title={file.StandardSolutionSection.Title}>{file.StandardSolutionSection.Title}</div>
                          <div>
                            <span className={styles.formatBadge} style={{ background: es.bg, color: es.color }}>
                              {ext}
                            </span>
                          </div>
                          <div className={styles.dateCell}>{formatDate(file.Modified)}</div>
                          <div className={styles.tableActions}>
                            <button
                              className={styles.btnDownload}
                              title={isDownloading ? `Скачивание…` : `Скачать`}
                              disabled={isDownloading}
                              onClick={() => file.AttachmentId && void downloadAttachment(file.AttachmentId)}
                            >
                              <i className={isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              <>
                {displayFiles.length === 0 ? (
                  <div className={styles.emptyState}>
                    <i className={`fa-regular fa-folder-open ${styles.emptyIcon}`} />
                    <span className={styles.emptyLabel}>
                      {isSearching
                        ? `По запросу «${searchQuery}» ничего не найдено`
                        : 'В этом разделе пока нет файлов'}
                    </span>
                  </div>
                ) : (
                  <div style={{ borderTop: '1px solid rgba(0,0,0,.06)' }}>
                    {displayFiles.map((file) => {
                      const ext = parseExt(file.Attachment?.Title);
                      const es = getExtStyle(ext);
                      return (
                        <div key={file.Id} className={styles.mobileFileRow}>
                          <span className={styles.mobileFileIcon} style={{ background: es.bg, color: es.color }}>
                            <i className={`fa-solid ${es.icon}`} />
                          </span>
                          <div className={styles.mobileFileInfo}>
                            <div className={styles.mobileFileName}>{file.Title}</div>
                            <div className={styles.mobileFileMeta}>
                              {file.StandardSolutionSection && (
                                <span className={styles.cipherCell}>{file.StandardSolutionSection.Title}</span>
                              )}
                              <span
                                className={styles.mobileFormatBadge}
                                style={{ background: es.bg, color: es.color }}
                              >
                                {ext}
                              </span>
                              <span className={styles.mobileFileDate}>{formatDate(file.Modified)}</span>
                            </div>
                          </div>
                          <button
                            className={styles.btnDownloadMobile}
                            title={isDownloading ? `Скачивание…` : `Скачать`}
                            disabled={isDownloading}
                            onClick={() => file.AttachmentId && void downloadAttachment(file.AttachmentId)}
                          >
                            <i className={isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`} style={{ color: '#3aae3a' }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {totalPages > 1 && (
              <div className={styles.pager}>
                <span className={styles.pagerInfo}>
                  {effectiveTotal > 0 ? `Показано ${from}–${to} из ${effectiveTotal}` : 'Нет файлов'}
                </span>
                <div className={styles.pagerButtons}>
                  <button
                    className={`${styles.pagerArrow} ${page <= 1 ? styles.pagerArrowDisabled : ''}`}
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    <i className="fa-solid fa-chevron-left" />
                  </button>
                  {pagerSeq.map((p, i) =>
                    p === '…' ? (
                      <span key={`dots-${i}`} className={`${styles.pagerBtn} ${styles.pagerBtnDots}`}>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        className={`${styles.pagerBtn} ${p === page ? styles.pagerBtnActive : ''}`}
                        onClick={() => handlePageChange(p as number)}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    className={`${styles.pagerArrow} ${page >= totalPages ? styles.pagerArrowDisabled : ''}`}
                    disabled={page >= totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    <i className="fa-solid fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
